import React, { useMemo, useEffect } from 'react';
import { Slate, withReact } from 'slate-react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { withLinks } from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/linkHelpers';
import withDeleteEmptyBlock from './helpers/withDeleteEmptyBlock';
import sanitizeHeadingNodes from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/sanitizeHeadingNodes';
import getRichTextCharCount from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/getRichTextCharCount';
import resetRichText from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/resetRichText';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import CardContent from '@material-ui/core/CardContent';
import FormHelperText from '@material-ui/core/FormHelperText';
import CharCount from 'atoms/CharCount';
import Card from 'pages/Homepage/components/molecules/WidgetConfigCard';
import SlateWrapper from './SlateWrapper';
import EditArea from './EditArea';
import Toolbar from './Toolbar';
import BoldButton from './BoldButton';
import ItalicButton from './ItalicButton';
import UnderlineButton from './UnderlineButton';
import StrikeThroughButton from './StrikeThroughButton';
import OrderedListButton from './OrderedListButton';
import UnorderedListButton from './UnorderedListButton';
import LinkButton from './LinkButton';
import { MAX_CHAR_COUNT_RICH_TEXT } from 'pages/Homepage/helpers/consts';

interface Props {
  value: Array<any>;
  originalValue: Array<any>;
  onSetFieldValue: (fieldKey: string, fieldValue: string) => void;
  shouldReset?: boolean;
  errors?: any;
  resetShouldReset: () => void;
}

function RichTextDefault({
  value,
  originalValue,
  onSetFieldValue,
  shouldReset,
  errors,
  resetShouldReset,
}: Props) {
  const handleChange = (editorValue: Array<any>) => {
    const sanitizedValue = sanitizeHeadingNodes(editorValue);

    // @ts-ignore (note - pearl) Formik typing is incorrectly saying second arg can only be string
    onSetFieldValue('settings.richTextDefault', sanitizedValue);
  };

  const count = getRichTextCharCount(value);

  const editor = useMemo(
    () => withDeleteEmptyBlock(withLinks(withReact(withHistory(createEditor())))),
    [],
  );

  useEffect(() => {
    if (shouldReset) {
      resetRichText(editor, originalValue);
      resetShouldReset();
    }
  }, [shouldReset]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h5">Default View Content</Typography>
        <Typography variant="subtitle2">
          This rich text content will appear in the widget when it is in its default, smaller view.
          We recommend keeping this content short so that it is all visible. On your homepage,
          hyperlinks will use your highlight color from your{' '}
          <Link href={'/admin/general/theming '} color="secondary">
            theme
          </Link>
          .
        </Typography>

        <SlateWrapper>
          <Slate editor={editor} initialValue={value} onChange={handleChange}>
            <Toolbar>
              <BoldButton />
              <ItalicButton />
              <UnderlineButton />
              <StrikeThroughButton />
              <OrderedListButton />
              <UnorderedListButton />
              <LinkButton editor={editor} />
            </Toolbar>
            <EditArea editor={editor} isDefaultEditor />
          </Slate>
        </SlateWrapper>
        {errors.richTextDefault && <FormHelperText error>{errors.richTextDefault}</FormHelperText>}
        <CharCount count={count} maxCount={MAX_CHAR_COUNT_RICH_TEXT} />
      </CardContent>
    </Card>
  );
}

export default RichTextDefault;
